/**
 * Utilities for safely accessing nested properties in objects
 */

/**
 * Safely get a nested property from an object using dot notation
 * @param obj - The object to traverse
 * @param path - Dot-separated path (e.g., "properties.web_ux_config.value")
 * @param defaultValue - Default value to return if path doesn't exist
 * @returns The value at the path or the default value
 */
export const getNestedProperty = <T = any>(
  obj: any,
  path: string,
  defaultValue: T | null = null
): T | null => {
  if (!obj || !path) {
    return defaultValue;
  }

  try {
    const parts = path.split(".");
    let current: any = obj;

    for (const part of parts) {
      if (part.startsWith("$")) {
        // Handle special cases if needed
        continue;
      }

      if (current && typeof current === "object" && part in current) {
        current = current[part];
      } else {
        return defaultValue;
      }
    }

    return current !== undefined ? current : defaultValue;
  } catch (e) {
    return defaultValue;
  }
};

/**
 * Check if a nested property exists in an object
 * @param obj - The object to check
 * @param path - Dot-separated path
 * @returns True if the path exists, false otherwise
 */
export const hasNestedProperty = (obj: any, path: string): boolean => {
  return getNestedProperty(obj, path, null) !== null;
};

/**
 * Get multiple nested properties at once
 * @param obj - The object to traverse
 * @param paths - Array of dot-separated paths
 * @param defaultValue - Default value for missing paths
 * @returns Object with path as key and value as value
 */
export const getMultipleNestedProperties = (
  obj: any,
  paths: string[],
  defaultValue: any = null
): Record<string, any> => {
  const result: Record<string, any> = {};
  paths.forEach((path) => {
    result[path] = getNestedProperty(obj, path, defaultValue);
  });
  return result;
};

